import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { bufferCount, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { getAddedValuesBy, getRemovedValuesBy } from 'src/util/array';
import { distinctUntilObjectChanged } from 'src/util/rxjs/operators';

export function getLengthChanges(formArray: UntypedFormArray): Observable<number> {
  return getValueChanges<any[]>(formArray).pipe(
    map((value) => value.length),
    distinctUntilChanged(),
  );
}

export function getStatusChanges(
  control: AbstractControl,
  startWithCurrentValue = true,
): Observable<'INVALID' | 'VALID' | 'PENDING' | 'DISABLED'> {
  return startWithCurrentValue
    ? control.statusChanges.pipe(startWith(control.status), distinctUntilChanged())
    : control.statusChanges.pipe(distinctUntilChanged());
}

export function getValueChanges<TValue = any>(
  control: AbstractControl,
  startWithCurrentValue = true,
): Observable<TValue> {
  const valueChanges: Observable<TValue> = control.valueChanges;
  return startWithCurrentValue
    ? valueChanges.pipe(startWith(control.value), distinctUntilObjectChanged())
    : valueChanges.pipe(distinctUntilObjectChanged());
}

export function getSelfWhenLengthChanges(formArray: UntypedFormArray) {
  return formArray.valueChanges.pipe(
    map((fa) => fa.length),
    distinctUntilChanged(),
    map(() => formArray),
    startWith(formArray),
  );
}

export function removeControlsWhere(
  formArray: UntypedFormArray,
  predicate: (control: AbstractControl) => boolean,
) {
  forEachControl(formArray, (c, i) => {
    const shouldRemove = predicate(c);
    if (shouldRemove) {
      formArray.removeAt(i);
    }
  });
}

export function forEachControl<TElementControl extends AbstractControl>(
  formArray: UntypedFormArray,
  action: (control: TElementControl, index: number) => void,
) {
  // NOTE: we are iterating starting with the last index,
  // this ensures if we remove the item it won't affect the
  // references of the remaining items in the iteration
  const lastIndex = formArray.length - 1;
  for (let i = lastIndex; i >= 0; i--) {
    const control = formArray.get([i]) as TElementControl;
    action(control, i);
  }
}

export function getAddedValuesChanges<T = any>(formArray: UntypedFormArray) {
  return getAddedValuesChangesBy<T>(formArray, (v) => v);
}

export function getAddedValuesChangesBy<T = any>(
  formArray: UntypedFormArray,
  selector: (value: T) => any,
) {
  return getDifferenceInValuesByImpl<T>(formArray, 'added', selector);
}

export function getRemovedValuesChanges<T = any>(formArray: UntypedFormArray) {
  return getRemovedValuesChangesBy<T>(formArray, (v) => v);
}

export function getRemovedValuesChangesBy<T = any>(
  formArray: UntypedFormArray,
  selector: (value: T) => any,
) {
  return getDifferenceInValuesByImpl<T>(formArray, 'removed', selector);
}

function getDifferenceInValuesByImpl<T>(
  formArray: UntypedFormArray,
  mode: 'added' | 'removed',
  selector: (value: T) => any,
): Observable<T[]> {
  return getValueChanges(formArray).pipe(
    bufferCount(2, 1),
    map((val) => {
      const [prev, curr] = val;
      return mode === 'added'
        ? getAddedValuesBy(prev, curr, selector)
        : getRemovedValuesBy(prev, curr, selector);
    }),
    filter((diffs) => diffs.length > 0),
  );
}

export function removeFirst(
  formArray: UntypedFormArray,
  predicate: (control: AbstractControl) => boolean,
) {
  const index = formArray.controls.findIndex(predicate);
  if (index !== -1) {
    formArray.removeAt(index);
  }
}

export function asFormControl(value: any): AbstractControl | null {
  const isFormControl =
    value instanceof UntypedFormControl ||
    value instanceof UntypedFormGroup ||
    value instanceof UntypedFormArray;
  return isFormControl ? (value as AbstractControl) : null;
}

export function getParentAtDepth(
  formControl: AbstractControl | UntypedFormArray | UntypedFormGroup | null,
  depth: number,
): AbstractControl | null {
  for (let i = 0; i < depth; i++) {
    formControl = formControl?.parent ?? null;
    if (formControl == null) {
      return null;
    }
  }
  return formControl;
}

export function getControls(control: UntypedFormGroup, paths: string[]): AbstractControl[] {
  const controls: AbstractControl[] = [];
  if (control == null) {
    return controls;
  }

  Object.keys(control.controls).forEach((path) => {
    if (paths.indexOf(path) !== -1) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      controls.push(control.get(path)!);
    }
  });
  return controls;
}
